/**
 * MarketMapLeaflet - Market Map Component using Leaflet
 * Thay thế MarketMapGoogle với custom markers hiển thị tên + giá
 *
 * Features:
 * - Custom card markers với tên + giá + icon loại BĐS
 * - Smart label placement tránh chồng chéo
 * - Zoom-based display (compact/full/cluster)
 * - Hover effects với scale
 * - Selected state với border + "Xem chi tiết" button
 * - Multiple tile layers (Light, Satellite, Hybrid, Street)
 * - Fullscreen toggle
 * - Auto fit bounds hoặc custom center/zoom
 */
import { useEffect, useRef, useState, useCallback, useMemo } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import marketMapService from '../../services/marketMapService'
import { TILE_LAYERS, GOOGLE_STYLE_MAP } from '../map/LeafletMap'

// Default center: Da Nang
const DEFAULT_CENTER = [16.0544, 108.2022]
const DEFAULT_ZOOM = 13

// Utility marker icons (emoji-based)
const UTILITY_ICONS = {
  default: '📍',
  school: '🏫',
  hospital: '🏥',
  bus_station: '🚌',
  police: '👮',
  government: '🏛️',
  bank: '🏦',
  market: '🏪',
  supermarket: '🛒',
  restaurant: '🍽️',
  cafe: '☕',
  hotel: '🏨',
  gas_station: '⛽',
  parking: '🅿️',
  pharmacy: '💊',
  church: '⛪',
  temple: '🛕',
  mosque: '🕌',
  park: '🌳',
  gym: '🏋️',
  stadium: '🏟️',
  airport: '✈️',
  train_station: '🚉',
  port: '⚓',
  bridge: '🌉',
  tower: '🗼',
  monument: '🗿',
  beach: '🏖️',
  mountain: '⛰️',
  atm: '💳',
  post_office: '📮',
  fire_station: '🚒',
  library: '📚',
  cinema: '🎬',
  mall: '🛍️',
}

// Create utility marker icon
function createUtilityIcon(iconType, name) {
  const emoji = UTILITY_ICONS[iconType] || UTILITY_ICONS.default

  return L.divIcon({
    className: 'utility-marker',
    html: `
      <div style="
        display: flex;
        flex-direction: column;
        align-items: center;
        filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3));
      ">
        <div style="
          font-size: 24px;
          line-height: 1;
          text-shadow: 0 1px 2px rgba(0,0,0,0.3);
        ">${emoji}</div>
        <div style="
          background: rgba(0,0,0,0.75);
          color: white;
          padding: 2px 6px;
          border-radius: 4px;
          font-size: 10px;
          font-weight: 500;
          white-space: nowrap;
          margin-top: 2px;
        ">${name || ''}</div>
      </div>
    `,
    iconSize: [100, 50],
    iconAnchor: [50, 12],
  })
}

// Property type icons (SVG)
const PROPERTY_ICONS = {
  apartment: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 3v18M15 3v18M3 9h18M3 15h18"/></svg>`,
  house: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>`,
  land: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M2 22h20"/><path d="M6 18V4l6 4 6-4v14"/></svg>`,
  villa: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 21h18"/><path d="M5 21V7l7-4 7 4v14"/><path d="M9 21v-6h6v6"/></svg>`,
  office: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="4" y="2" width="16" height="20" rx="2"/><path d="M9 22v-4h6v4"/><path d="M8 6h.01M12 6h.01M16 6h.01M8 10h.01M12 10h.01M16 10h.01M8 14h.01M12 14h.01M16 14h.01"/></svg>`,
  default: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>`,
}

// Get property icon based on type
function getPropertyIcon(propertyType) {
  const type = (propertyType || '').toLowerCase()
  if (type.includes('chung cư') || type.includes('apartment') || type.includes('căn hộ')) return PROPERTY_ICONS.apartment
  if (type.includes('nhà phố') || type.includes('house') || type.includes('nhà')) return PROPERTY_ICONS.house
  if (type.includes('đất') || type.includes('land') || type.includes('nền')) return PROPERTY_ICONS.land
  if (type.includes('villa') || type.includes('biệt thự')) return PROPERTY_ICONS.villa
  if (type.includes('văn phòng') || type.includes('office') || type.includes('shophouse')) return PROPERTY_ICONS.office
  return PROPERTY_ICONS.default
}

// Truncate name helper
function truncateName(name, maxLength = 16) {
  if (!name) return ''
  return name.length > maxLength ? name.substring(0, maxLength) + '…' : name
}

// Format short price (for compact mode)
function formatShortPrice(priceText) {
  if (!priceText) return '?'
  // If already short, return as is
  if (priceText.length <= 12) return priceText
  // Try to extract just the number part
  const match = priceText.match(/(\d+[\d.,]*)\s*(tỷ|triệu|tr)/i)
  if (match) {
    return `${match[1]}${match[2].toLowerCase() === 'tỷ' ? 'tỷ' : 'tr'}`
  }
  return priceText.substring(0, 10) + '…'
}

// Display modes based on zoom level
const DISPLAY_MODES = {
  CLUSTER: 'cluster',    // zoom < 12: show clusters
  COMPACT: 'compact',    // zoom 12-14: small badges
  NORMAL: 'normal',      // zoom 14-16: card markers
  DETAILED: 'detailed',  // zoom > 16: full cards with more info
}

function getDisplayMode(zoom) {
  if (zoom < 12) return DISPLAY_MODES.CLUSTER
  if (zoom < 14) return DISPLAY_MODES.COMPACT
  if (zoom < 16) return DISPLAY_MODES.NORMAL
  return DISPLAY_MODES.DETAILED
}

// Create COMPACT marker (small badge with price only)
function createCompactMarkerHTML(markerData, isSelected = false) {
  const price = formatShortPrice(markerData.price_text || marketMapService.formatPrice(markerData.price_value))
  const color = markerData.marker_color || marketMapService.getMarkerColor(markerData.price_value)
  const isFeatured = markerData.is_featured

  return `
    <div class="compact-marker ${isSelected ? 'selected' : ''}" style="
      position: relative;
      filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3));
      transition: all 0.2s ease;
      ${isSelected ? 'transform: scale(1.15); z-index: 1000;' : ''}
    ">
      <div style="
        background: ${color};
        color: white;
        padding: 4px 8px;
        border-radius: 12px;
        font-size: 11px;
        font-weight: 700;
        white-space: nowrap;
        display: flex;
        align-items: center;
        gap: 3px;
        border: 2px solid ${isSelected ? 'white' : 'rgba(255,255,255,0.5)'};
        ${isSelected ? 'box-shadow: 0 0 0 2px ' + color + ';' : ''}
      ">
        ${isFeatured ? '⭐' : ''}
        ${price}
      </div>
      <div style="
        position: absolute;
        bottom: -4px;
        left: 50%;
        transform: translateX(-50%);
        width: 0; height: 0;
        border-left: 4px solid transparent;
        border-right: 4px solid transparent;
        border-top: 4px solid ${color};
      "></div>
    </div>
  `
}

// Create NORMAL card marker HTML (improved)
function createMarkerHTML(markerData, isSelected = false, displayMode = DISPLAY_MODES.NORMAL) {
  const price = markerData.price_text || marketMapService.formatPrice(markerData.price_value)
  const color = markerData.marker_color || marketMapService.getMarkerColor(markerData.price_value)
  const name = truncateName(markerData.name, displayMode === DISPLAY_MODES.DETAILED ? 22 : 16)
  const isFeatured = markerData.is_featured
  const propertyIcon = getPropertyIcon(markerData.property_type)
  const area = markerData.area_text || (markerData.area ? `${markerData.area}m²` : '')

  // Use compact mode for small zoom
  if (displayMode === DISPLAY_MODES.COMPACT) {
    return createCompactMarkerHTML(markerData, isSelected)
  }

  const showArea = displayMode === DISPLAY_MODES.DETAILED && area

  return `
    <div class="card-marker ${isSelected ? 'selected' : ''}" style="
      position: relative;
      filter: drop-shadow(0 3px 8px rgba(0,0,0,0.25));
      transition: all 0.2s ease;
      ${isSelected ? 'transform: scale(1.08);' : ''}
    ">
      <div style="
        background: white;
        border-radius: 10px;
        overflow: hidden;
        min-width: ${displayMode === DISPLAY_MODES.DETAILED ? '150px' : '125px'};
        max-width: ${displayMode === DISPLAY_MODES.DETAILED ? '180px' : '160px'};
        border: 2.5px solid ${isSelected ? '#2563eb' : color};
        ${isSelected ? 'box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.25);' : ''}
      ">
        <!-- Header -->
        <div style="
          background: linear-gradient(135deg, ${color} 0%, ${adjustColor(color, -20)} 100%);
          padding: 6px 10px;
          display: flex;
          align-items: center;
          gap: 5px;
        ">
          ${isFeatured ? `<span style="font-size: 11px;">⭐</span>` : `<span style="color: white; opacity: 0.9;">${propertyIcon}</span>`}
          <span style="
            color: white;
            font-size: 11px;
            font-weight: 600;
            line-height: 1.3;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            flex: 1;
            text-shadow: 0 1px 2px rgba(0,0,0,0.2);
          ">${name}</span>
        </div>

        <!-- Price Section -->
        <div style="
          padding: ${showArea ? '5px 10px 4px' : '8px 10px'};
          background: white;
          text-align: center;
        ">
          <div style="
            color: ${color};
            font-size: ${displayMode === DISPLAY_MODES.DETAILED ? '14px' : '13px'};
            font-weight: 700;
            letter-spacing: -0.3px;
          ">${price}</div>
          ${showArea ? `
            <div style="
              color: #6b7280;
              font-size: 10px;
              margin-top: 2px;
            ">${area}</div>
          ` : ''}
        </div>

        <!-- Action Button (selected only) -->
        ${isSelected ? `
          <div style="
            padding: 5px 8px 6px;
            background: #f8fafc;
            border-top: 1px solid #e2e8f0;
          ">
            <button
              class="view-detail-btn"
              data-marker-id="${markerData.id}"
              style="
                width: 100%;
                padding: 6px 10px;
                background: linear-gradient(135deg, ${color} 0%, ${adjustColor(color, -15)} 100%);
                color: white;
                font-size: 11px;
                font-weight: 600;
                border: none;
                border-radius: 6px;
                cursor: pointer;
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 4px;
                transition: all 0.2s;
              "
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                <circle cx="12" cy="12" r="3"/>
              </svg>
              Xem chi tiết
            </button>
          </div>
        ` : ''}
      </div>

      <!-- Arrow -->
      <div style="
        position: absolute;
        bottom: -8px;
        left: 50%;
        transform: translateX(-50%);
        width: 0; height: 0;
        border-left: 8px solid transparent;
        border-right: 8px solid transparent;
        border-top: 8px solid ${isSelected ? '#2563eb' : color};
      "></div>
    </div>
  `
}

// Helper to darken/lighten color
function adjustColor(color, amount) {
  const hex = color.replace('#', '')
  const num = parseInt(hex, 16)
  const r = Math.min(255, Math.max(0, (num >> 16) + amount))
  const g = Math.min(255, Math.max(0, ((num >> 8) & 0x00FF) + amount))
  const b = Math.min(255, Math.max(0, (num & 0x0000FF) + amount))
  return `#${(1 << 24 | r << 16 | g << 8 | b).toString(16).slice(1)}`
}

// Create cluster icon
function createClusterIcon(count, colors) {
  const dominantColor = colors[0] || '#3B82F6'
  const size = Math.min(50, 30 + Math.sqrt(count) * 5)

  return L.divIcon({
    className: 'cluster-marker',
    html: `
      <div style="
        width: ${size}px;
        height: ${size}px;
        background: linear-gradient(135deg, ${dominantColor} 0%, ${adjustColor(dominantColor, -30)} 100%);
        border: 3px solid white;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-weight: 700;
        font-size: ${size > 40 ? '14px' : '12px'};
        box-shadow: 0 3px 10px rgba(0,0,0,0.3);
      ">${count}</div>
    `,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  })
}

// CSS for path animations
const PATH_ANIMATION_STYLES = `
  @keyframes dash-march {
    to { stroke-dashoffset: -20; }
  }
  @keyframes path-pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
  @keyframes path-glow {
    0%, 100% { filter: drop-shadow(0 0 2px var(--stroke-color, #3B82F6)); }
    50% { filter: drop-shadow(0 0 8px var(--stroke-color, #3B82F6)) drop-shadow(0 0 12px var(--stroke-color, #3B82F6)); }
  }
  .path-animation-march path,
  .path-animation-march polyline {
    animation: dash-march 0.5s linear infinite;
  }
  .path-animation-pulse path,
  .path-animation-pulse polyline {
    animation: path-pulse 1.5s ease-in-out infinite;
  }
  .path-animation-glow path,
  .path-animation-glow polyline {
    animation: path-glow 2s ease-in-out infinite;
  }
`

// Inject path animation styles
let pathStylesInjected = false
function injectPathStyles() {
  if (pathStylesInjected) return
  const style = document.createElement('style')
  style.textContent = PATH_ANIMATION_STYLES
  document.head.appendChild(style)
  pathStylesInjected = true
}

// Get dash array based on stroke style
function getDashArray(strokeStyle) {
  switch (strokeStyle) {
    case 'dashed': return '10, 10'
    case 'dotted': return '2, 8'
    default: return null
  }
}

// Simple clustering algorithm
function clusterMarkers(markers, zoom, map) {
  if (zoom >= 12 || !map) return null // No clustering at higher zoom

  const clusters = []
  const used = new Set()

  // Cluster radius in pixels (smaller at higher zoom)
  const clusterRadius = zoom < 10 ? 80 : 50

  markers.forEach((marker, i) => {
    if (used.has(i)) return

    const point = map.latLngToContainerPoint([
      parseFloat(marker.latitude),
      parseFloat(marker.longitude),
    ])

    const cluster = {
      markers: [marker],
      center: { lat: parseFloat(marker.latitude), lng: parseFloat(marker.longitude) },
      colors: [marker.marker_color || '#3B82F6'],
    }

    // Find nearby markers
    markers.forEach((other, j) => {
      if (i === j || used.has(j)) return

      const otherPoint = map.latLngToContainerPoint([
        parseFloat(other.latitude),
        parseFloat(other.longitude),
      ])

      const distance = Math.sqrt(
        Math.pow(point.x - otherPoint.x, 2) +
        Math.pow(point.y - otherPoint.y, 2)
      )

      if (distance < clusterRadius) {
        cluster.markers.push(other)
        cluster.colors.push(other.marker_color || '#3B82F6')
        used.add(j)
      }
    })

    used.add(i)

    if (cluster.markers.length > 1) {
      // Calculate cluster center
      const latSum = cluster.markers.reduce((sum, m) => sum + parseFloat(m.latitude), 0)
      const lngSum = cluster.markers.reduce((sum, m) => sum + parseFloat(m.longitude), 0)
      cluster.center = {
        lat: latSum / cluster.markers.length,
        lng: lngSum / cluster.markers.length,
      }
      clusters.push(cluster)
    } else {
      // Single marker - not clustered
      clusters.push({ markers: [marker], center: cluster.center, colors: cluster.colors, single: true })
    }
  })

  return clusters
}

// Smart label offset to reduce overlap
// Returns offset for the LABEL only, not the pin marker
function calculateLabelOffset(markerData, allMarkers, map) {
  if (!map || allMarkers.length < 2) return { x: 0, y: 0, hasOffset: false }

  const myPoint = map.latLngToContainerPoint([
    parseFloat(markerData.latitude),
    parseFloat(markerData.longitude),
  ])

  let offsetX = 0
  let offsetY = 0
  const threshold = 140 // pixels - detection range for overlap

  allMarkers.forEach((other) => {
    if (other.id === markerData.id) return

    const otherPoint = map.latLngToContainerPoint([
      parseFloat(other.latitude),
      parseFloat(other.longitude),
    ])

    const dx = myPoint.x - otherPoint.x
    const dy = myPoint.y - otherPoint.y
    const distance = Math.sqrt(dx * dx + dy * dy)

    if (distance < threshold && distance > 0) {
      // Push label away from nearby marker
      const pushStrength = (threshold - distance) / threshold * 40
      offsetX += (dx / distance) * pushStrength
      offsetY += (dy / distance) * pushStrength
    }
  })

  // Clamp offsets (larger range for better separation)
  const clampedX = Math.max(-60, Math.min(60, offsetX))
  const clampedY = Math.max(-50, Math.min(50, offsetY))

  // Only apply offset if significant
  const hasOffset = Math.abs(clampedX) > 10 || Math.abs(clampedY) > 10

  return {
    x: hasOffset ? clampedX : 0,
    y: hasOffset ? clampedY : 0,
    hasOffset,
  }
}

// Create pin marker (fixed position dot)
function createPinIcon(color, isSelected = false) {
  const size = isSelected ? 14 : 10
  return L.divIcon({
    className: 'pin-marker',
    html: `
      <div style="
        width: ${size}px;
        height: ${size}px;
        background: ${color};
        border: 2px solid white;
        border-radius: 50%;
        box-shadow: 0 2px 4px rgba(0,0,0,0.3);
        ${isSelected ? 'box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.4), 0 2px 4px rgba(0,0,0,0.3);' : ''}
      "></div>
    `,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  })
}

// Create card label icon (can be offset from pin)
// The offset transforms the visual position while keeping the marker at the same lat/lng
function createCardLabelIcon(markerData, isSelected = false, displayMode = DISPLAY_MODES.NORMAL, offset = { x: 0, y: 0, hasOffset: false }) {
  const html = createMarkerHTML(markerData, isSelected, displayMode)
  const color = markerData.marker_color || marketMapService.getMarkerColor(markerData.price_value)

  const isCompact = displayMode === DISPLAY_MODES.COMPACT
  const width = isCompact ? 80 : (displayMode === DISPLAY_MODES.DETAILED ? 180 : 150)
  const height = isCompact ? 30 : (isSelected ? 110 : 75)

  // When offset exists, we draw a connector line from the card's arrow tip to the pin
  // The SVG line goes from the card (which is offset) back to where the pin is (origin)
  const connectorHtml = offset.hasOffset ? `
    <div class="connector-container" style="
      position: absolute;
      left: 50%;
      bottom: -8px;
      pointer-events: none;
      z-index: -1;
    ">
      <svg width="${Math.max(Math.abs(offset.x) * 2 + 10, 20)}" height="${Math.max(Math.abs(offset.y) + 20, 30)}" style="
        position: absolute;
        left: 50%;
        transform: translateX(-50%);
        overflow: visible;
      ">
        <line
          x1="${Math.abs(offset.x) + 5}"
          y1="8"
          x2="${Math.abs(offset.x) + 5 - offset.x}"
          y2="${Math.max(Math.abs(offset.y), 15) - offset.y + 8}"
          stroke="${color}"
          stroke-width="2"
          stroke-opacity="0.5"
          stroke-dasharray="4,4"
        />
      </svg>
    </div>
  ` : ''

  return L.divIcon({
    className: `card-marker-wrapper ${isCompact ? 'compact' : ''} ${offset.hasOffset ? 'has-offset' : ''}`,
    html: `
      <div class="card-label-container" style="
        position: relative;
        transform: translate(${offset.x}px, ${offset.y}px);
        transition: transform 0.3s ease;
      ">
        ${html}
        ${connectorHtml}
      </div>
    `,
    iconSize: [width, height],
    iconAnchor: [width / 2, height],
    popupAnchor: [0, -height],
  })
}

function MarketMapLeaflet({
  markers = [],
  utilityMarkers = [], // Utility markers (schools, hospitals, etc.)
  paths = [], // NEW: paths/shapes to display
  selectedMarker,
  onMarkerClick,
  onMarkerHover,
  onViewDetail,
  center = { lat: 16.0544, lng: 108.2022 },
  zoom = 13,
  mapStyle = 'hybrid',
  useCustomCenter = false,
  onFullscreenChange,
  fullscreenTargetRef,
}) {
  const containerRef = useRef(null)
  const mapRef = useRef(null)
  const markersLayerRef = useRef(null)
  const utilityMarkersLayerRef = useRef(null) // Layer for utility markers
  const pathsLayerRef = useRef(null) // NEW: layer for paths
  const markersMapRef = useRef(new Map()) // markerData.id -> L.Marker
  const clustersLayerRef = useRef(null) // For cluster markers
  const [mapLoaded, setMapLoaded] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [currentZoom, setCurrentZoom] = useState(zoom)
  const [displayMode, setDisplayMode] = useState(getDisplayMode(zoom))
  const initialFitDoneRef = useRef(false)

  // Convert center format
  const centerLatLng = useMemo(() => {
    if (Array.isArray(center)) return center
    if (center && typeof center === 'object') {
      return [center.lat || DEFAULT_CENTER[0], center.lng || DEFAULT_CENTER[1]]
    }
    return DEFAULT_CENTER
  }, [center])

  // Convert Google map style to Leaflet tile layer
  const tileLayerKey = useMemo(() => {
    return GOOGLE_STYLE_MAP[mapStyle] || 'cartodb_light'
  }, [mapStyle])

  // Initialize map
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return

    // Inject path animation styles
    injectPathStyles()

    const map = L.map(containerRef.current, {
      center: centerLatLng,
      zoom: zoom,
      zoomControl: true,
      attributionControl: true,
    })

    mapRef.current = map

    // Add tile layer
    const tileConfig = TILE_LAYERS[tileLayerKey] || TILE_LAYERS.cartodb_light
    L.tileLayer(tileConfig.url, {
      attribution: tileConfig.attribution,
      maxZoom: 19,
    }).addTo(map)

    // Create paths layer group (below markers)
    pathsLayerRef.current = L.layerGroup().addTo(map)

    // Create clusters layer (for cluster mode)
    clustersLayerRef.current = L.layerGroup().addTo(map)

    // Create utility markers layer (below property markers)
    utilityMarkersLayerRef.current = L.layerGroup().addTo(map)

    // Create markers layer group (on top)
    markersLayerRef.current = L.layerGroup().addTo(map)

    // Track zoom changes for display mode
    map.on('zoomend', () => {
      const newZoom = map.getZoom()
      setCurrentZoom(newZoom)
      setDisplayMode(getDisplayMode(newZoom))
    })

    setMapLoaded(true)

    // Cleanup
    return () => {
      if (mapRef.current) {
        mapRef.current.remove()
        mapRef.current = null
      }
      markersMapRef.current.clear()
    }
  }, [])

  // Update tile layer when mapStyle changes
  useEffect(() => {
    if (!mapRef.current || !mapLoaded) return

    const map = mapRef.current
    const tileConfig = TILE_LAYERS[tileLayerKey] || TILE_LAYERS.cartodb_light

    // Remove existing tile layers
    map.eachLayer((layer) => {
      if (layer instanceof L.TileLayer) {
        map.removeLayer(layer)
      }
    })

    // Add new tile layer (below markers)
    L.tileLayer(tileConfig.url, {
      attribution: tileConfig.attribution,
      maxZoom: 19,
    }).addTo(map)

    // Re-add markers layer on top
    if (markersLayerRef.current) {
      markersLayerRef.current.addTo(map)
    }
  }, [tileLayerKey, mapLoaded])

  // Update center/zoom when props change (if using custom center)
  useEffect(() => {
    if (!mapRef.current || !mapLoaded || !useCustomCenter) return
    mapRef.current.setView(centerLatLng, zoom, { animate: false })
  }, [centerLatLng, zoom, mapLoaded, useCustomCenter])

  // Update markers when data or display mode changes
  useEffect(() => {
    if (!mapLoaded || !markersLayerRef.current) return

    const map = mapRef.current
    const markersLayer = markersLayerRef.current
    const clustersLayer = clustersLayerRef.current

    // Clear existing markers and clusters
    markersLayer.clearLayers()
    clustersLayer?.clearLayers()
    markersMapRef.current.clear()

    // Create bounds for fitBounds
    const bounds = L.latLngBounds([])

    // Handle clustering mode
    if (displayMode === DISPLAY_MODES.CLUSTER && markers.length > 0) {
      const clusters = clusterMarkers(markers, currentZoom, map)

      if (clusters) {
        clusters.forEach((cluster) => {
          if (cluster.single) {
            // Single marker - render normally
            const markerData = cluster.markers[0]
            renderSingleMarker(markerData, map, markersLayer, bounds)
          } else {
            // Cluster marker
            const clusterMarker = L.marker([cluster.center.lat, cluster.center.lng], {
              icon: createClusterIcon(cluster.markers.length, cluster.colors),
            })

            // Click to zoom in
            clusterMarker.on('click', () => {
              const clusterBounds = L.latLngBounds(
                cluster.markers.map((m) => [parseFloat(m.latitude), parseFloat(m.longitude)])
              )
              map.fitBounds(clusterBounds, { padding: [50, 50] })
            })

            clustersLayer?.addLayer(clusterMarker)
            bounds.extend([cluster.center.lat, cluster.center.lng])
          }
        })
      }
    } else {
      // Normal rendering with display mode
      markers.forEach((markerData) => {
        renderSingleMarker(markerData, map, markersLayer, bounds)
      })
    }

    // Fit bounds only on initial load if not using custom center
    if (markers.length > 0 && !initialFitDoneRef.current && !useCustomCenter) {
      if (bounds.isValid()) {
        map.fitBounds(bounds, { padding: [80, 80], maxZoom: 15 })
        initialFitDoneRef.current = true
      }
    } else if (useCustomCenter) {
      initialFitDoneRef.current = true
    }

    // Helper function to render single marker with Pin + Label architecture
    function renderSingleMarker(markerData, map, layer, bounds) {
      if (!markerData.latitude || !markerData.longitude) return

      const lat = parseFloat(markerData.latitude)
      const lng = parseFloat(markerData.longitude)
      const isSelected = selectedMarker?.id === markerData.id
      const color = markerData.marker_color || marketMapService.getMarkerColor(markerData.price_value)

      // Calculate offset for label (to avoid overlap)
      const offset = calculateLabelOffset(markerData, markers, map)
      const shouldOffset = (displayMode === DISPLAY_MODES.NORMAL || displayMode === DISPLAY_MODES.DETAILED) && offset.hasOffset

      // 1. Create PIN marker (always at exact coordinates - never moves)
      const pinMarker = L.marker([lat, lng], {
        icon: createPinIcon(color, isSelected),
        zIndexOffset: isSelected ? 900 : (markerData.is_featured ? 400 : -100), // Below label
        interactive: false, // Pin is not clickable, only visual indicator
      })

      // 2. Create LABEL marker (card with info - can be offset)
      const labelMarker = L.marker([lat, lng], {
        icon: createCardLabelIcon(markerData, isSelected, displayMode, shouldOffset ? offset : { x: 0, y: 0, hasOffset: false }),
        zIndexOffset: isSelected ? 1000 : (markerData.is_featured ? 500 : 0),
      })

      // Click handler on label
      labelMarker.on('click', (e) => {
        L.DomEvent.stopPropagation(e)
        if (onMarkerClick) {
          onMarkerClick(markerData)
        }
      })

      // Store reference (to label marker for updates)
      labelMarker.markerData = markerData
      labelMarker.pinMarker = pinMarker // Reference to pin for cleanup
      labelMarker.currentOffset = shouldOffset ? offset : { x: 0, y: 0, hasOffset: false }
      markersMapRef.current.set(markerData.id, labelMarker)

      // Add pin first (below), then label (on top)
      if (shouldOffset) {
        layer.addLayer(pinMarker)
      }
      layer.addLayer(labelMarker)

      bounds.extend([lat, lng])
    }
  }, [markers, mapLoaded, onMarkerClick, useCustomCenter, displayMode, currentZoom])

  // Render paths/shapes
  useEffect(() => {
    if (!mapLoaded || !pathsLayerRef.current) return

    const pathsLayer = pathsLayerRef.current

    // Clear existing paths
    pathsLayer.clearLayers()

    // Add each path
    paths.forEach((pathData) => {
      if (!pathData.geojson) return

      const strokeColor = pathData.stroke_color || '#3B82F6'
      const fillColor = pathData.fill_color || '#DBEAFE'
      const strokeWidth = pathData.stroke_width || 3
      const strokeOpacity = pathData.stroke_opacity || 1
      const fillOpacity = pathData.fill_opacity || 0.3
      const strokeStyle = pathData.stroke_style || 'solid'
      const animation = pathData.animation || 'none'

      const layerStyle = {
        color: strokeColor,
        fillColor: fillColor,
        weight: strokeWidth,
        opacity: strokeOpacity,
        fillOpacity: fillOpacity,
        dashArray: getDashArray(strokeStyle),
      }

      try {
        const geojsonLayer = L.geoJSON(pathData.geojson, {
          style: layerStyle,
          onEachFeature: (feature, layer) => {
            // Add animation class if needed
            if (animation && animation !== 'none') {
              layer.on('add', () => {
                const element = layer.getElement()
                if (element) {
                  element.classList.add(`path-animation-${animation}`)
                  element.style.setProperty('--stroke-color', strokeColor)
                }
              })
            }

            // Popup with path name
            if (pathData.name) {
              layer.bindPopup(`<strong>${pathData.name}</strong>`, {
                closeButton: false,
                className: 'path-popup',
              })
            }
          },
        })

        pathsLayer.addLayer(geojsonLayer)
      } catch (error) {
        console.error('Failed to render path:', pathData.id, error)
      }
    })
  }, [paths, mapLoaded])

  // Render utility markers
  useEffect(() => {
    if (!mapLoaded || !utilityMarkersLayerRef.current) return

    const utilityLayer = utilityMarkersLayerRef.current

    // Clear existing utility markers
    utilityLayer.clearLayers()

    // Add each utility marker
    utilityMarkers.forEach((utilityMarker) => {
      if (!utilityMarker.latitude || !utilityMarker.longitude || !utilityMarker.is_active) return

      const lat = parseFloat(utilityMarker.latitude)
      const lng = parseFloat(utilityMarker.longitude)

      const marker = L.marker([lat, lng], {
        icon: createUtilityIcon(utilityMarker.icon_type, utilityMarker.name),
        zIndexOffset: -200, // Below property markers
      })

      // Simple tooltip on hover
      marker.bindTooltip(utilityMarker.name, {
        permanent: false,
        direction: 'top',
        offset: [0, -20],
      })

      utilityLayer.addLayer(marker)
    })
  }, [utilityMarkers, mapLoaded])

  // Update marker icons when selection changes (without recreating all markers)
  useEffect(() => {
    if (!mapLoaded) return

    markersMapRef.current.forEach((labelMarker, id) => {
      const markerData = labelMarker.markerData
      const isSelected = selectedMarker?.id === id
      const offset = labelMarker.currentOffset || { x: 0, y: 0, hasOffset: false }
      const color = markerData.marker_color || marketMapService.getMarkerColor(markerData.price_value)

      // Update label icon with current display mode and offset
      labelMarker.setIcon(createCardLabelIcon(markerData, isSelected, displayMode, offset))
      labelMarker.setZIndexOffset(isSelected ? 1000 : (markerData.is_featured ? 500 : 0))

      // Update pin icon if exists
      if (labelMarker.pinMarker) {
        labelMarker.pinMarker.setIcon(createPinIcon(color, isSelected))
        labelMarker.pinMarker.setZIndexOffset(isSelected ? 900 : (markerData.is_featured ? 400 : -100))
      }
    })

    // Setup view detail button click handlers
    setTimeout(() => {
      document.querySelectorAll('.view-detail-btn').forEach((btn) => {
        btn.onclick = (e) => {
          e.stopPropagation()
          const markerId = parseInt(btn.dataset.markerId)
          const marker = markersMapRef.current.get(markerId)
          if (marker && marker.markerData && onViewDetail) {
            onViewDetail(marker.markerData)
          }
        }
      })
    }, 0)
  }, [selectedMarker, mapLoaded, onViewDetail, displayMode])

  // Pan to selected marker
  useEffect(() => {
    if (!mapLoaded || !mapRef.current || !selectedMarker) return

    if (selectedMarker.latitude && selectedMarker.longitude) {
      mapRef.current.panTo([
        parseFloat(selectedMarker.latitude),
        parseFloat(selectedMarker.longitude),
      ])
    }
  }, [selectedMarker, mapLoaded])

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isNowFullscreen = !!document.fullscreenElement
      setIsFullscreen(isNowFullscreen)
      if (onFullscreenChange) {
        onFullscreenChange(isNowFullscreen)
      }

      // Invalidate map size after fullscreen change
      setTimeout(() => {
        if (mapRef.current) {
          mapRef.current.invalidateSize()
        }
      }, 100)
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange)
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange)

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange)
    }
  }, [onFullscreenChange])

  // Toggle fullscreen
  const toggleFullscreen = useCallback(() => {
    const target = fullscreenTargetRef?.current || containerRef.current
    if (!target) return

    if (!document.fullscreenElement) {
      target.requestFullscreen?.() || target.webkitRequestFullscreen?.()
    } else {
      document.exitFullscreen?.() || document.webkitExitFullscreen?.()
    }
  }, [fullscreenTargetRef])

  return (
    <div ref={containerRef} className="w-full h-full relative">
      {/* Custom Fullscreen Button */}
      {mapLoaded && (
        <button
          onClick={toggleFullscreen}
          className="absolute top-3 right-3 z-[500] p-2.5 bg-white rounded-lg shadow-md hover:bg-gray-100 transition-colors"
          title={isFullscreen ? 'Thoát toàn màn hình' : 'Toàn màn hình'}
        >
          {isFullscreen ? (
            <svg className="w-5 h-5 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <svg className="w-5 h-5 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
            </svg>
          )}
        </button>
      )}

      {/* Map Layer Switcher */}
      {mapLoaded && (
        <div className="absolute top-3 right-14 z-[500] flex gap-1">
          {[
            { key: 'cartodb_light', label: 'Map' },
            { key: 'satellite', label: 'Vệ tinh' },
          ].map((layer) => (
            <button
              key={layer.key}
              onClick={() => {
                const map = mapRef.current
                if (!map) return

                // Update tile layer
                map.eachLayer((l) => {
                  if (l instanceof L.TileLayer) {
                    map.removeLayer(l)
                  }
                })

                const tileConfig = TILE_LAYERS[layer.key]
                L.tileLayer(tileConfig.url, {
                  attribution: tileConfig.attribution,
                  maxZoom: 19,
                }).addTo(map)

                // Re-add markers layer
                if (markersLayerRef.current) {
                  markersLayerRef.current.addTo(map)
                }
              }}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                tileLayerKey === layer.key
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100 shadow-md'
              }`}
            >
              {layer.label}
            </button>
          ))}
        </div>
      )}

      {/* Loading overlay */}
      {!mapLoaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10">
          <div className="text-center">
            <div className="animate-spin w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full mx-auto mb-2" />
            <p className="text-gray-500 text-sm">Đang tải bản đồ...</p>
          </div>
        </div>
      )}
    </div>
  )
}

export default MarketMapLeaflet
